<script setup>
import { computed, defineEmits, defineProps, onBeforeUnmount, onMounted, ref, watch } from "vue";
import apiClient from "@/apiClient";

const props = defineProps({
  customer: { type: Object, required: false },
  editable: { type: Boolean, required: false },
  addresses: { type: Array, required: false },
  emitAddressObjects: { type: Boolean, required: false, default: false },
  layout: { type: String, default: 'default' },
  defaultCenter: {
    type: Object,
    required: false,
    default: () => ({ lat: 51.5072, lng: -0.1276 }),
  },
  defaultZoom: { type: Number, required: false, default: 11 },
  singlePointZoom: { type: Number, required: false, default: 14 },
  multiplePointZoom: { type: Number, required: false, default: 10 },
});

const emit = defineEmits(["update:customer", "add:address"]);

const newAddress = ref("");
const mapCenter = ref({ ...(props.defaultCenter ?? { lat: 51.5072, lng: -0.1276 }) });
const markerPosition = ref(null);
const confirmedLocation = ref(null);
const saving = ref(false);
const error = ref(null);
const mapZoom = ref(props.defaultZoom ?? 11);
const addressMarkers = ref([]);
const geocodeInProgress = ref(false);
const geocodeCache = ref({});
const geocodeRunId = ref(0);
let googleMapsRetryTimer = null;

const usingExternalAddresses = computed(() => Array.isArray(props.addresses));
const normalizeAddressKey = (value) => (value ?? "")
  .toString()
  .trim()
  .toLowerCase()
  .replace(/\s+/g, " ");
const normalizeAddress = (address, index) => {
  if (typeof address === "string") {
    return {
      id: `external-${index}`,
      address_id: null,
      data: address,
      latitude: null,
      longitude: null,
      base_distance_miles: null,
      base_duration_minutes: null,
    };
  }

  const numericAddressId = address?.address_id
    ?? (/^\d+$/.test(String(address?.id ?? "")) ? address.id : null);

  return {
    id: numericAddressId ?? address?.id ?? `external-${index}`,
    address_id: numericAddressId,
    data: address?.data ?? "",
    latitude: address?.latitude ?? null,
    longitude: address?.longitude ?? null,
    base_distance_miles: address?.base_distance_miles ?? null,
    base_duration_minutes: address?.base_duration_minutes ?? null,
  };
};
const addresses = computed(() => (
  usingExternalAddresses.value
    ? (props.addresses ?? []).map(normalizeAddress).filter((address) => address.data?.trim())
    : (props.customer?.address ?? [])
));
const addressSignature = computed(() => addresses.value
  .map((address) => [
    address?.id ?? "",
    address?.address_id ?? "",
    address?.data ?? "",
    address?.latitude ?? "",
    address?.longitude ?? "",
    address?.base_distance_miles ?? "",
    address?.base_duration_minutes ?? "",
  ].join("|"))
  .join("||"));
const displayedMarkers = computed(() => {
  const markers = [...addressMarkers.value];
  if (markerPosition.value) {
    const duplicate = markers.some((marker) => (
      Math.abs(marker.position.lat - markerPosition.value.lat) < 0.00001
      && Math.abs(marker.position.lng - markerPosition.value.lng) < 0.00001
    ));
    if (!duplicate) {
      markers.push({
        id: "pending-location",
        position: { ...markerPosition.value },
        title: "Pending location",
      });
    }
  }
  return markers;
});
const hasMapPoints = computed(() => displayedMarkers.value.length > 0);
const primaryId = computed(() => props.customer?.primary_address ?? null);
const hasCustomer = computed(() => !!props.customer?.id);
const formatTravelFromBase = (address) => {
  const distance = Number(address?.base_distance_miles ?? NaN);
  const duration = Number(address?.base_duration_minutes ?? NaN);
  const parts = [];

  if (Number.isFinite(distance)) {
    parts.push(`${distance.toFixed(1)} miles from base`);
  }

  if (Number.isFinite(duration)) {
    parts.push(`${Math.round(duration)} mins drive`);
  }

  return parts.join(" / ");
};

const toCoordinates = (latitude, longitude) => {
  const hasLatitude = latitude !== null && latitude !== undefined && `${latitude}`.trim() !== "";
  const hasLongitude = longitude !== null && longitude !== undefined && `${longitude}`.trim() !== "";
  if (!hasLatitude || !hasLongitude) return null;

  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const geocodeLookup = async (geocoder, query) => new Promise((resolve) => {
  geocoder.geocode({ address: query }, (results, status) => {
    if (status !== "OK" || !results?.length) {
      resolve(null);
      return;
    }

    const point = results[0]?.geometry?.location;
    if (!point) {
      resolve(null);
      return;
    }

    resolve({ lat: point.lat(), lng: point.lng() });
  });
});

const persistAddressCoordinates = async (address, coords) => {
  const addressId = address?.address_id ?? null;
  if (!addressId || !coords) {
    return;
  }

  if (toCoordinates(address?.latitude, address?.longitude)) {
    return;
  }

  try {
    await apiClient.put(`/api/address/${addressId}`, {
      data: address.data,
      latitude: coords.lat,
      longitude: coords.lng,
    });
  } catch (err) {
    console.error(err);
  }
};

const updateMapViewport = (markers) => {
  const points = markers.map((marker) => marker.position);
  if (markerPosition.value) {
    points.push(markerPosition.value);
  }
  if (!points.length) return;

  if (points.length === 1) {
    mapCenter.value = { ...points[0] };
    mapZoom.value = props.singlePointZoom ?? 14;
    return;
  }

  const totals = points.reduce((acc, point) => ({
    lat: acc.lat + point.lat,
    lng: acc.lng + point.lng,
  }), { lat: 0, lng: 0 });

  mapCenter.value = {
    lat: totals.lat / points.length,
    lng: totals.lng / points.length,
  };
  mapZoom.value = props.multiplePointZoom ?? 10;
};

const refreshAddressMarkers = async () => {
  const runId = ++geocodeRunId.value;
  geocodeInProgress.value = false;

  if (!addresses.value.length) {
    addressMarkers.value = [];
    mapCenter.value = { ...(props.defaultCenter ?? { lat: 51.5072, lng: -0.1276 }) };
    mapZoom.value = props.defaultZoom ?? 11;
    return;
  }

  const geocoder = typeof window !== "undefined" && window.google?.maps?.Geocoder
    ? new window.google.maps.Geocoder()
    : null;

  const nextMarkers = [];

  for (let index = 0; index < addresses.value.length; index += 1) {
    const address = addresses.value[index];
    const existingCoords = toCoordinates(address?.latitude, address?.longitude);
    if (existingCoords) {
      nextMarkers.push({
        id: `address-${address?.id ?? index}`,
        position: existingCoords,
        title: address?.data ?? "Address",
      });
      continue;
    }

    const label = (address?.data ?? "").trim();
    if (!label) continue;

    const cacheKey = normalizeAddressKey(label);
    const cached = geocodeCache.value[cacheKey];
    if (cached && Number.isFinite(cached.lat) && Number.isFinite(cached.lng)) {
      nextMarkers.push({
        id: `address-${address?.id ?? index}`,
        position: { lat: cached.lat, lng: cached.lng },
        title: label,
      });
      continue;
    }
    if (cached === null || !geocoder) {
      continue;
    }

    geocodeInProgress.value = true;
    const lookedUp = await geocodeLookup(geocoder, label);
    if (runId !== geocodeRunId.value) return;

    geocodeCache.value[cacheKey] = lookedUp ? { ...lookedUp } : null;
    if (lookedUp) {
      await persistAddressCoordinates(address, lookedUp);
      nextMarkers.push({
        id: `address-${address?.id ?? index}`,
        position: lookedUp,
        title: label,
      });
    }
  }

  if (runId !== geocodeRunId.value) return;
  geocodeInProgress.value = false;
  addressMarkers.value = nextMarkers;
  updateMapViewport(nextMarkers);
};

const geocodeAddress = async () => {
  error.value = null;
  const value = newAddress.value.trim();
  if (!value) {
    error.value = "Enter an address.";
    return;
  }

  if (!window.google?.maps?.Geocoder) {
    error.value = "Google Maps is unavailable. You can still add the location manually.";
    return;
  }

  const geocoder = new window.google.maps.Geocoder();
  geocoder.geocode({ address: value }, (results, status) => {
    if (status !== "OK" || !results?.length) {
      error.value = "Unable to locate that address on the map.";
      return;
    }

    const location = results[0].geometry.location;
    const coords = { lat: location.lat(), lng: location.lng() };
    mapCenter.value = coords;
    mapZoom.value = 14;
    markerPosition.value = coords;
    confirmedLocation.value = null;
  });
};

const confirmLocation = () => {
  if (!markerPosition.value) return;
  confirmedLocation.value = { ...markerPosition.value };
};

const addAddress = async () => {
  error.value = null;
  const trimmedAddress = newAddress.value.trim();
  if (!trimmedAddress) {
    error.value = "Address is required.";
    return;
  }

  if (usingExternalAddresses.value) {
    const targetKey = normalizeAddressKey(trimmedAddress);
    const duplicate = addresses.value.some((address) => normalizeAddressKey(address?.data) === targetKey);
    if (duplicate) {
      error.value = "That venue location is already in the list.";
      return;
    }

    emit("add:address", props.emitAddressObjects
      ? {
        data: trimmedAddress,
        latitude: confirmedLocation.value?.lat ?? markerPosition.value?.lat ?? null,
        longitude: confirmedLocation.value?.lng ?? markerPosition.value?.lng ?? null,
      }
      : trimmedAddress);
    newAddress.value = "";
    markerPosition.value = null;
    confirmedLocation.value = null;

    return;
  }

  if (!hasCustomer.value) {
    error.value = "Select a customer before adding an address.";
    return;
  }

  saving.value = true;
  try {
    const response = await apiClient.post("/api/address", {
      data: trimmedAddress,
      latitude: confirmedLocation.value?.lat ?? null,
      longitude: confirmedLocation.value?.lng ?? null,
    });
    const address = response.data;
    await apiClient.post(`/api/customer/${props.customer.id}/attach-address`, {
      address_id: address.id,
    });
    const nextCustomer = {
      ...props.customer,
      address: [...(props.customer.address ?? []), address],
    };
    emit("update:customer", nextCustomer);
    newAddress.value = "";
    markerPosition.value = null;
    confirmedLocation.value = null;
  } catch (err) {
    error.value = err.response?.data?.message ?? "Unable to save address.";
  } finally {
    saving.value = false;
  }
};

watch(
  addressSignature,
  () => {
    void refreshAddressMarkers();
  },
  { immediate: true },
);

watch(
  markerPosition,
  () => {
    updateMapViewport(addressMarkers.value);
  },
  { deep: true },
);

const scheduleGoogleMapsRetry = () => {
  if (googleMapsRetryTimer || typeof window === "undefined") {
    return;
  }

  googleMapsRetryTimer = window.setInterval(() => {
    if (!addresses.value.length) {
      return;
    }

    if (!window.google?.maps?.Geocoder) {
      return;
    }

    const needsLookup = addresses.value.some((address) => {
      const label = (address?.data ?? "").trim();
      if (!label) return false;
      if (toCoordinates(address?.latitude, address?.longitude)) return false;

      const cacheKey = normalizeAddressKey(label);
      return geocodeCache.value[cacheKey] === undefined;
    });

    if (!needsLookup && addressMarkers.value.length > 0) {
      window.clearInterval(googleMapsRetryTimer);
      googleMapsRetryTimer = null;
      return;
    }

    void refreshAddressMarkers();
  }, 1000);
};

onMounted(() => {
  scheduleGoogleMapsRetry();
});

onBeforeUnmount(() => {
  if (googleMapsRetryTimer && typeof window !== "undefined") {
    window.clearInterval(googleMapsRetryTimer);
    googleMapsRetryTimer = null;
  }
});
</script>

<template>
  <div :class="{ 'address-manager--overview': layout === 'overview' }">
  <div class="grid min-w-0 gap-4" :class="layout === 'overview' ? 'address-overview-layout' : 'xl:grid-cols-[minmax(18rem,0.8fr)_minmax(24rem,1.2fr)]'">
    <div :class="{ 'order-2': layout !== 'overview' }" class="address-map-panel min-w-0 space-y-2 self-start rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div class="flex items-center justify-between gap-2">
        <div class="text-sm font-semibold text-slate-700 dark:text-slate-200">Location Map</div>
        <div v-if="geocodeInProgress" class="text-xs text-slate-500 dark:text-slate-400">Locating points…</div>
      </div>
      <div v-if="hasMapPoints" class="address-map-canvas h-72 overflow-hidden rounded-md border border-slate-200 dark:border-slate-700 sm:h-80 xl:aspect-[4/3] xl:h-auto">
        <GMapMap class="h-full w-full"
          :center="mapCenter" :zoom="mapZoom">
          <GMapMarker
            v-for="marker in displayedMarkers"
            :key="marker.id"
            :position="marker.position"
            :title="marker.title"
          />
        </GMapMap>
      </div>
      <div v-else class="address-map-canvas flex h-72 items-center justify-center rounded-md bg-slate-50 px-4 text-center text-xs text-slate-500 dark:bg-slate-800/60 dark:text-slate-400 sm:h-80 xl:aspect-[4/3] xl:h-auto">
        Map points appear once an address can be located.
      </div>
    </div>

    <div :class="{ 'order-1': layout !== 'overview' }" class="min-w-0 space-y-3">
      <div v-if="!usingExternalAddresses && !hasCustomer" class="text-sm text-slate-500 dark:text-slate-400">
        Select a customer to manage addresses.
      </div>

      <div v-else class="space-y-3">
        <div v-if="!addresses.length" class="text-sm text-slate-500 dark:text-slate-400">
          {{ usingExternalAddresses ? "No venue addresses detected yet." : "No addresses attached yet." }}
        </div>
        <div v-else class="grid grid-cols-1 gap-2" :class="layout === 'overview' ? 'address-overview-list' : 'sm:grid-cols-2 xl:grid-cols-1'">
          <div v-for="address in addresses" :key="address.id"
            class="min-w-0 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60">
            <div class="break-words text-sm leading-snug text-slate-700 dark:text-slate-200">{{ address.data }}</div>
            <div v-if="address.id === primaryId" class="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Primary</div>
            <div v-if="address.latitude && address.longitude" class="mt-1 text-xs text-slate-400">
              {{ Number(address.latitude).toFixed(5) }}, {{ Number(address.longitude).toFixed(5) }}
            </div>
            <div v-if="formatTravelFromBase(address)" class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {{ formatTravelFromBase(address) }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="usingExternalAddresses && editable" class="space-y-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        <div class="text-sm font-semibold text-slate-700 dark:text-slate-200">Add Venue Location</div>
        <textarea class="w-full rounded border bg-white p-2 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          rows="3" v-model="newAddress"></textarea>
        <div class="flex flex-wrap gap-2">
          <button class="btn" type="button" @click.prevent="geocodeAddress">
            Find on map
          </button>
          <button class="btn accent" type="button" @click.prevent="addAddress">
            Add location
          </button>
        </div>
        <div v-if="markerPosition" class="text-xs text-emerald-600 dark:text-emerald-400">
          Location found at {{ markerPosition.lat.toFixed(5) }}, {{ markerPosition.lng.toFixed(5) }}
        </div>
        <div v-if="error" class="text-sm text-rose-500">{{ error }}</div>
      </div>

      <div v-if="!usingExternalAddresses" class="space-y-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        <div class="text-sm font-semibold text-slate-700 dark:text-slate-200">Add New Address</div>
        <textarea class="w-full rounded border bg-white p-2 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          rows="3" v-model="newAddress" :disabled="!editable"></textarea>
        <div class="flex flex-wrap gap-2">
          <button class="btn" type="button" @click.prevent="geocodeAddress" :disabled="!editable">
            Find on map
          </button>
          <button class="btn" type="button" @click.prevent="confirmLocation" :disabled="!editable || !markerPosition">
            Confirm location
          </button>
          <button class="btn accent" type="button" @click.prevent="addAddress" :disabled="!editable || saving">
            Save address
          </button>
        </div>
        <div v-if="error" class="text-sm text-rose-500">{{ error }}</div>
        <div v-if="confirmedLocation" class="text-xs text-emerald-600 dark:text-emerald-400">
          Location confirmed at {{ confirmedLocation.lat.toFixed(5) }}, {{ confirmedLocation.lng.toFixed(5) }}
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<style scoped>
.address-manager--overview { container: site-addresses / inline-size; min-width: 0; }
.address-manager--overview .address-map-panel { padding: 0; border: 0; }
.address-manager--overview .address-map-canvas {
  height: clamp(12rem, 36cqi, 18rem);
  aspect-ratio: auto;
  border-radius: 0.75rem;
}
.address-overview-layout { grid-template-columns: minmax(0, 1fr); align-items: start; }
</style>
